/**
 * Confined Space Alternate Entry Component
 * =======================================
 * 
 * Individual form component for confined space alternate entry permits.
 * 
 * Base Components:
 * - EXTENDS BaseFormComponent<ConfinedSpaceAlternateEntryForm> for form logic inheritance
 * - USES FormLayoutComponent for step navigation UI (in template)
 * 
 * Form Configuration:
 * - Form structure, steps, sections, and subsections are defined in the form model
 * - Signature and upload requirements are configured via form model metadata
 * - Component implements abstract methods: initializeForm() and getFormData()
 * - Component overrides setupDefaultStepValidation() for custom step validation rules
 * 
 * Key Functionality:
 * - Creates nested form groups for logical organization (basic, hazards, ventilation, additional)
 * - Implements dynamic field management for expandable hazard and monitoring sections
 * - Manages complex state arrays for showing/hiding conditional fields (up to 5 physical hazards, 2 atmospheric hazards, 3 monitoring results each)
 * - Provides add/remove functionality for hazard and monitoring entries with maximum limits
 * - Implements step validation with signature requirements for final step
 * - Flattens nested form structure for backend submission
 * 
 * Unique Complex Logic:
 * - Dynamic field expansion system with boolean arrays controlling field visibility
 * - Counter management for multiple hazard types (physical, atmospheric) and monitoring results (oxygen, hydrogen sulfide, carbon monoxide, LEL)
 * - Conditional field rendering based on user interaction with add buttons
 * - Maximum limit enforcement (5 physical hazards, 2 atmospheric hazards, 3 monitoring results each)
 * 
 * Form Model vs Component Responsibilities:
 * - FORM MODEL: Defines step/section structure, field mappings, attachment requirements, and form metadata
 * - COMPONENT: Handles Angular form validation, form group creation, dynamic field logic, and overrides base form methods
 * 
 * @author MacSafety Development Team
 * @version 1.0
 */
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { BaseFormComponent } from '../../base-form.component';
import { FormStep } from '../../../../../models/forms/form-steps.interface';
import { ConfinedSpaceAlternateEntryForm } from '../../../../../models/forms/confined-space-alt-entry/confined-space-alt-entry.model';
import { FormService } from '../../../../../services/form.service';
import { EntraService } from '../../../../../services/entra.service';
import { AttachmentUploadService } from '../../../../../services/attachment-upload.service';
import { SignatureService } from '../../../../../services/signature.service';
import { ToastService } from '../../../../../services/toast.service';
import { ScreenSizeService } from '../../../../../services/screen-size.service';
import { FormModelService } from '../../../../../services/form-model.service';  

@Component({
  selector: 'app-confined-space-alt-entry',
  templateUrl: './confined-space-alt-entry.component.html',
  styleUrls: ['./confined-space-alt-entry.component.css'],
})
export class ConfinedSpaceAltEntryComponent
  extends BaseFormComponent<ConfinedSpaceAlternateEntryForm>
  implements OnInit
{
  override formType = ConfinedSpaceAlternateEntryForm.getFormInfo().formType;
  override steps: FormStep[] = ConfinedSpaceAlternateEntryForm.getFormSteps();
  override model = new ConfinedSpaceAlternateEntryForm();

  physicalHazardCount: number = 1;
  showAddPhysicalHazardButton: boolean = true;
 showPhysicalHazardIndex: Array<boolean> = [true, false, false, false, false];

  atmosphericHazardCount: number = 1;
  showAddAtmosphericHazardButton: boolean = true;
  showAtmosphericHazardIndex: Array<boolean> = [true, false];

  oxygenResultCount: number = 1;
  showAddOxygenResultButton: boolean = true;
  showOxygenResultIndex: Array<boolean> = [true, false, false];

  hydrogenSulfideResultCount: number = 1;
  showAddHydrogenSulfideResultButton: boolean = true;
  showHydrogenSulfideResultIndex: Array<boolean> = [true, false, false];

  carbonMonoxideResultCount: number = 1;
  showAddCarbonMonoxideResultButton: boolean = true;
  showCarbonMonoxideResultIndex: Array<boolean> = [true, false, false];

  lowerExplosiveLimitResultCount: number = 1;
  showAddLowerExplosiveLimitResultButton: boolean = true;
  showLowerExplosiveLimitResultIndex: Array<boolean> = [true, false, false];

  // Form group getters
  get basicFormGroup(): FormGroup {
    return this.form.get('basic') as FormGroup;
  }

  get hazardsFormGroup(): FormGroup {
    return this.form.get('hazards') as FormGroup;
  }

  get ventilationFormGroup(): FormGroup {
    return this.form.get('ventilation') as FormGroup;
  }

  get additionalFormGroup(): FormGroup {
    return this.form.get('additional') as FormGroup;
  }

  constructor(
    protected override fb: FormBuilder,
    protected override formService: FormService,
    protected override router: Router,
    protected override entraService: EntraService,
    protected override attachmentService: AttachmentUploadService,
    protected override toastService: ToastService,
    protected override signatureService: SignatureService,
    protected override screenSizeService: ScreenSizeService,
    protected formModelService: FormModelService
  ) {
    super(
      fb,
      formService,
      router,
      entraService,
      attachmentService,
      toastService,
      signatureService,
      screenSizeService
    );

    this.formMetadata = this.formModelService.getFormMetadata(this.formType)!;
  }

  override ngOnInit(): void {
    super.ngOnInit();
  }

  protected override initializeForm(): void {
    this.form = this.fb.group({
      basic: this.fb.group({
        spaceLocation: [this.model.spaceLocation, Validators.required],
        entryDate: [this.model.entryDate, Validators.required],
        entryDuration: [this.model.entryDuration],
        entrant1: [this.model.entrant1],
        entrant2: [this.model.entrant2],
      }),
      hazards: this.fb.group({
        physicalHazard1: [this.model.physicalHazard1],
        physicalHazardAction1: [this.model.physicalHazardAction1],
        physicalHazardActionDescription1: [this.model.physicalHazardActionDescription1],
        physicalHazard2: [this.model.physicalHazard2],
        physicalHazardAction2: [this.model.physicalHazardAction2],
        physicalHazardActionDescription2: [this.model.physicalHazardActionDescription2],
        physicalHazard3: [this.model.physicalHazard3],
        physicalHazardAction3: [this.model.physicalHazardAction3],
        physicalHazardActionDescription3: [this.model.physicalHazardActionDescription3],
        physicalHazard4: [this.model.physicalHazard4],
        physicalHazardAction4: [this.model.physicalHazardAction4],
        physicalHazardActionDescription4: [this.model.physicalHazardActionDescription4],
        physicalHazard5: [this.model.physicalHazard5],
        physicalHazardAction5: [this.model.physicalHazardAction5],
        physicalHazardActionDescription5: [this.model.physicalHazardActionDescription5],
        atmosphericHazard1: [this.model.atmosphericHazard1],
        atmosphericHazardAction1: [this.model.atmosphericHazardAction1],
        atmosphericHazardActionDescription1: [this.model.atmosphericHazardActionDescription1],
        atmosphericHazard2: [this.model.atmosphericHazard2],
        atmosphericHazardAction2: [this.model.atmosphericHazardAction2],
        atmosphericHazardActionDescription2: [this.model.atmosphericHazardActionDescription2],
      }),
      ventilation: this.fb.group({
        forcedAirVentilationRequired: [this.model.forcedAirVentilationRequired, Validators.required],
        ventilationType: [this.model.ventilationType],
        multipleUnitsRequired: [this.model.multipleUnitsRequired, Validators.required],
        oxygenResult1: [this.model.oxygenResult1],
        oxygenResult2: [this.model.oxygenResult2],
        oxygenResult3: [this.model.oxygenResult3],
        hydrogenSulfideResult1: [this.model.hydrogenSulfideResult1],
        hydrogenSulfideResult2: [this.model.hydrogenSulfideResult2],
        hydrogenSulfideResult3: [this.model.hydrogenSulfideResult3],
        carbonMonoxideResult1: [this.model.carbonMonoxideResult1],
        carbonMonoxideResult2: [this.model.carbonMonoxideResult2],
        carbonMonoxideResult3: [this.model.carbonMonoxideResult3],
        lowerExplosiveLimitResult1: [this.model.lowerExplosiveLimitResult1],
        lowerExplosiveLimitResult2: [this.model.lowerExplosiveLimitResult2],
        lowerExplosiveLimitResult3: [this.model.lowerExplosiveLimitResult3],
        gasDetectorModel: [this.model.gasDetectorModel],
        calibrationDaysLeft: [this.model.calibrationDaysLeft],
      }),
      additional: this.fb.group({
        additionalNotes: [this.model.additionalNotes],
        responsiblePersonName: [this.model.responsiblePersonName, Validators.required],
        responsiblePersonJobTitle: [this.model.responsiblePersonJobTitle, Validators.required],
      })
    });
  }

  protected override getFormData(): ConfinedSpaceAlternateEntryForm {
    const formValue = this.form.value;
    // Flatten the nested form groups into a single object
    const flattenedData = {
      ...formValue.basic,
      ...formValue.hazards,
      ...formValue.ventilation,
      ...formValue.additional
    };
    const confinedSpaceAlternateEntryForm = new ConfinedSpaceAlternateEntryForm(flattenedData);
    const updatedConfinedSpaceAlternateEntryForm = this.updateModelWithAttachments(confinedSpaceAlternateEntryForm);
    return updatedConfinedSpaceAlternateEntryForm;
  }

   protected override setupDefaultStepValidation(): void {
    // Skip validation setup if noFormRequirements is true
    if (!this.shouldApplyFormRequirements()) {
      return;
    }
    // Define step indices
    const SIGNATURE_REQUIREMENT_INDEX: number = 0;

    // Custom validation rules for each step using the form groups directly
    this.setupStepValidation('basic', this.basicFormGroup);
    this.setupStepValidation('hazards', this.hazardsFormGroup);
    this.setupStepValidation('ventilation', this.ventilationFormGroup);
    this.setupStepValidation('additional', this.additionalFormGroup, {
      signatureIndex: [SIGNATURE_REQUIREMENT_INDEX],
    });
  }

  addPhysicalHazard() {
    if (this.physicalHazardCount < 5) {
      this.physicalHazardCount++;
      const hazardCount = this.physicalHazardCount++
      this.showPhysicalHazardIndex[hazardCount-1] = true;
      this.physicalHazardCount = hazardCount;
      if (hazardCount === 5) {
        this.showAddPhysicalHazardButton = false;
      }
    } 
   
  }

  addAtmosphericHazard() {
    if (this.atmosphericHazardCount < 2) {
      this.atmosphericHazardCount++;
      const atmosphericHazardCount = this.atmosphericHazardCount++
      this.showAtmosphericHazardIndex[atmosphericHazardCount-1] = true;
      this.atmosphericHazardCount = atmosphericHazardCount;
      if (atmosphericHazardCount === 2) {
        this.showAddAtmosphericHazardButton = false;
      }
    }
  }

  addOxygenResult() {
    if (this.oxygenResultCount < 3) {
      this.oxygenResultCount++;
      const oxygenResultCount = this.oxygenResultCount++
      this.showOxygenResultIndex[oxygenResultCount-1] = true;
      this.oxygenResultCount = oxygenResultCount;
      if (oxygenResultCount === 3) {
        this.showAddOxygenResultButton = false;
      }
    }
  }

  addHydrogenSulfideResult() {
    if (this.hydrogenSulfideResultCount < 3) {
      this.hydrogenSulfideResultCount++;
      const hydrogenSulfideResultCount = this.hydrogenSulfideResultCount++
      this.showHydrogenSulfideResultIndex[hydrogenSulfideResultCount-1] = true;
      this.hydrogenSulfideResultCount = hydrogenSulfideResultCount;
      if (hydrogenSulfideResultCount === 3) {
        this.showAddHydrogenSulfideResultButton = false;
      }
    }
  }

  addCarbonMonoxideResult() {
    if (this.carbonMonoxideResultCount < 3) {
      this.carbonMonoxideResultCount++;
      const carbonMonoxideResultCount = this.carbonMonoxideResultCount++
      this.showCarbonMonoxideResultIndex[carbonMonoxideResultCount-1] = true;
      this.carbonMonoxideResultCount = carbonMonoxideResultCount;
      if (carbonMonoxideResultCount === 3) {
        this.showAddCarbonMonoxideResultButton = false;
      }
    }
  }

  addLowerExplosiveLimitResult() {
    if (this.lowerExplosiveLimitResultCount < 3) {
      this.lowerExplosiveLimitResultCount++;
      const lowerExplosiveLimitResultCount = this.lowerExplosiveLimitResultCount++
      this.showLowerExplosiveLimitResultIndex[lowerExplosiveLimitResultCount-1] = true;
      this.lowerExplosiveLimitResultCount = lowerExplosiveLimitResultCount;
      if (lowerExplosiveLimitResultCount === 3) {
        this.showAddLowerExplosiveLimitResultButton = false;
      }
    }
  }
  
}